import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { cn } from "@utils/tw"
import { defineBlock, EditableText } from "eddev/blocks"
import { useEffect, useRef, useState } from "react"
import { motion } from "motion/react"
import { Appear } from "@components/motion/Appear"
import { InsightTile } from "@components/atoms/InsightTile"

export const meta: BlockMeta = {
  title: "Media & Text",
  tags: ["#page"],
}

export default defineBlock("content/media-and-text", (props) => {
  const [selected, setSelected] = useState(0)
  const interval = useRef<number>()

  const clearTimer = () => {
    if (interval.current) {
      clearInterval(interval.current)
    }
  }

  const setTimer = () => {
    interval.current = window.setInterval(() => {
      setSelected((prev) => {
        return (prev + 1) % (props.textCarousel?.length || 0)
      })
    }, 4000)
  }

  const updateSelected = (index: number) => {
    setSelected(index)
    clearTimer()
    setTimer()
  }

  useEffect(() => {
    clearTimer()
    if (!props.textCarousel || props.textCarousel.length === 0) {
      return
    }
    setTimer()
    return () => {
      clearTimer()
    }
  })

  return (
    <div className="content-sized my-blockSpace grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-4">
      <Appear className="w-full aspect-square bg-white rounded-sm overflow-hidden relative">
        {props.image?.mediaItemUrl && !props.video?.mediaItemUrl && (
          <ResponsiveImage {...props.image} className="w-full h-full" objectFit="cover" />
        )}
        {props.video?.mediaItemUrl && (
          <video className="w-full h-full object-cover scale-[1.15]" autoPlay loop muted playsInline>
            <source src={props.video.mediaItemUrl} type="video/mp4" />
          </video>
        )}
      </Appear>
      <div className="w-full flex flex-col self-stretch justify-between items-center gap-5 md:gap-4">
        <Appear
          delay={150}
          className="w-full h-full bg-slate200 rounded-sm p-5 flex flex-col gap-7 md:gap-0 md:justify-between"
        >
          <Appear slide delay={200}>
            <EditableText
              store="mediaText"
              className="type-serif-2-xl-regular text-balance"
              defaultValue="The energy of a new financial ecosystem."
              placeholder="Media & Text Title"
            />
          </Appear>

          <div className="flex flex-col gap-4">
            {props.textCarousel?.map(
              (textItem, index) =>
                !!textItem && (
                  <motion.div
                    key={index}
                    className={cn(
                      "flex flex-col gap-2 pl-4 pr-6 border-l border-l-slate950 overflow-hidden cursor-pointer transition-colors duration-300 text-slate950",
                      index === selected ? "" : "text-slate400",
                    )}
                    animate={{ height: index === selected ? "auto" : "1.5em" }}
                    onClick={() => updateSelected(index)}
                  >
                    <p className="type-sans-base-semibold text-pretty">{textItem.title}</p>
                    <p
                      className={cn(
                        "type-sans-base-regular text-slate700 text-pretty opacity-50",
                        index === selected && "opacity-100 text-slate950",
                      )}
                    >
                      {textItem.body}
                    </p>
                  </motion.div>
                ),
            )}
          </div>
        </Appear>

        {props.highlightedInsight && (
          <InsightTile
            id={props.highlightedInsight.id}
            uri={props.highlightedInsight.uri || ""}
            theme={props.highlightedInsight.insightHeader?.theme!}
            image={props.highlightedInsight.insightHeader?.featuredImage || undefined}
            title={props.highlightedInsight.title || ""}
            type={props.highlightedInsight.insightHeader?.type?.insightType?.titleSingular || undefined}
            typeUri={props.highlightedInsight.insightHeader?.type?.uri || undefined}
            date={props.highlightedInsight.insightHeader?.displayedDate || undefined}
            delay={300}
            small
          />
        )}
      </div>
    </div>
  )
})
